import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { Regional } from "@shared/entities/reg_regionals.entity";
import { Group } from "@shared/entities/reg_groups.entity";
import { Alert } from "@shared/interfaces/alert.interface";
import { Driver } from "@shared/interfaces/driver.interface";
import { AlertType } from "@shared/interfaces/alert-type.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
import { VehicleType } from "@shared/interfaces/vehicle-type.interface";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { PaginationProperties, StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
import { ProcessedAlertHistory } from "@shared/interfaces/processed-alert-history.interface";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, MenuMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
import { vehicleFilterLabel } from "@utils/vehicle-filter-label";
// endregion Imports - Utils
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Components
import ModalFormScheduledReportDescription from "@components/Modal/FormScheduledReportDescription";
// endregion Imports - Components
// region Imports - Organisms
import GenericQueryFilter, {
  IDateRangeOptions,
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    alertData: ProcessedAlertHistory[],
    startDate: string,
    finishDate: string,
    pagination?: PaginationProperties
  ) => void;
  loadMoreToggle: boolean;
  generateScheduleReport: {
    openModal: boolean;
    onCloseModal: () => void;
  }
}
// endregion Interfaces

const QueryFilterAlertHistory: React.FC<IProps> = ({ open, onClose, onFilter, loadMoreToggle, generateScheduleReport }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([] as Array<Vehicle>);
  const [optionsVehicleTypeFilter, setOptionsVehicleTypeFilter] = useState<VehicleType[]>([] as Array<VehicleType>);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<Driver[]>([] as Array<Driver>);
  const [optionsAlertTypeFilter, setOptionsAlertTypeFilter] = useState<AlertType[]>([] as Array<AlertType>);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<Plant[]>([] as Array<Plant>);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<Regional[]>([] as Array<Regional>);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<Group[]>([] as Array<Group>);

  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingVehicleTypeFilter, setLoadingVehicleTypeFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(false);
  const [loadingAlertTypeFilter, setLoadingAlertTypeFilter] = useState(false);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);

  const [loadingAlerts, setLoadingAlerts] = useState(false);
  // endregion States

  // region Constants
  const paramNameStartDate = "startDate";
  const paramNameEndDate = "finishDate";
  const paramNameVehicle = "idVehicles";
  const paramNameVehicleType = "idVehicleTypes";
  const paramNameDriver = "idDrivers";
  const paramNameAlertType = "idAlertTypes";
  const paramNameRegional = "idRegionals";
  const paramNamePlant = "idPlants";
  const recordLimitPerFetch = 200;

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(FilterMessages.optionVehicles),
    paramName: paramNameVehicle,
    // eslint-disable-next-line max-len
    values: optionsVehicleFilter.map((vehicle) => ({ value: vehicle.id_vehicle, label: vehicleFilterLabel(vehicle) }))
  }), [loadingVehicleFilter, optionsVehicleFilter, t]);

  const vehicleTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleTypeFilter,
    labelName: t(FilterMessages.optionVehicleTypes),
    paramName: paramNameVehicleType,
    values: optionsVehicleTypeFilter.map((vehicleType) => ({ value: vehicleType.id_vehicle_type!, label: vehicleType.description }))
  }), [loadingVehicleTypeFilter, optionsVehicleTypeFilter, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(MenuMessages.drivers),
    paramName: paramNameDriver,
    values: optionsDriverFilter.map((driver) => ({
      value: driver.id_driver!,
      label: `${driver.name} - ${driver.cpf}`
    }))
  }), [loadingDriverFilter, optionsDriverFilter, t]);

  const alertTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingAlertTypeFilter,
    labelName: t(FilterMessages.optionAlertTypes),
    paramName: paramNameAlertType,
    values: optionsAlertTypeFilter.map((alertType) => ({ value: alertType.id_alert_type!, label: alertType.description }))
  }), [loadingAlertTypeFilter, optionsAlertTypeFilter, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant!.location!.id_location!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, t]);

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(MenuMessages.regionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map(
      (regional) => ({
        value: regional.id_regional!,
        label: `${regional.code} - ${regional.name}`
      })
    )
  }), [loadingRegionalFilter, optionsRegionalFilter, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(MenuMessages.groups),
    paramName: "idGroups",
    values: optionsGroupFilter.map(
      (group) => ({
        value: group.id_group!,
        label: `${group.code} - ${group.name}`
      })
    )
  }), [loadingGroupFilter, optionsGroupFilter, t]);

  const defaultStartDate = dateFns.subDays(new Date(), 1);

  defaultStartDate.setHours(0, 0, 0, 0);

  const defaultEndDate = new Date();

  defaultEndDate.setHours(23, 59, 59, 999);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: utils.convertDateToISOWithTimezone(defaultEndDate)
    }
  };

  const initialParamsFilter: IParamQueryValues[] = [
    { paramName: paramNameStartDate, paramValue: utils.convertDateToISOWithTimezone(defaultStartDate) },
    { paramName: paramNameEndDate, paramValue: utils.convertDateToISOWithTimezone(defaultEndDate) }
  ];
  // endregion Constants

  // region Refs
  const paramsFilterRef = useRef<IParamQueryValues[]>(initialParamsFilter);
  const offsetRef = useRef<number|undefined>(undefined);
  // endregion Refs

  // region Functions

  /**
   * Load vehicle options from the API.
   */
  const loadVehicleOptions = async () => {

    try {

      // Get all vehicles
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingVehicleFilter(false);
    }
  };

  /**
   * Load vehicle type options from the API.
   */
  const loadVehicleTypeOptions = async () => {
    try {

      // Get all alert types
      const { data } = await api.get("vehicles/types/read", { params: { translate: true } });

      if (data.status === "success") {
        setOptionsVehicleTypeFilter(data.result);

        return;
      }

      setOptionsVehicleTypeFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleTypeFilter(false);
    }
  };

  /**
   * Load alert type options from the API.
   */
  const loadAlertTypeOptions = async () => {
    try {

      // Get all alert types
      const { data } = await api.get("alerts/types/read");

      if (data.status === "success") {
        setOptionsAlertTypeFilter(data.result);

        return;
      }

      setOptionsAlertTypeFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingAlertTypeFilter(false);
    }
  };

  /**
   * Load driver options from the API.
   */
  const loadDriverOptions = async () => {
    try {

      // Get all drivers
      const { data } = await api.get("drivers/read");

      if (data.status === "success") setOptionsDriverFilter(data.result as Driver[]);
      else setOptionsDriverFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingDriverFilter(false);
    }
  };

  /**
   * Load plant options from the API.
   */
  const loadPlantOptions = async () => {
    try {

      // Get all plants
      const { data } = await api.get("plants/read", { params: { linkedLocation: true } });

      if (data.status === "success") setOptionsPlantFilter(data.result);
      else setOptionsPlantFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlantFilter(false);
    }
  };

  /**
   * Load regional options from the API.
   */
  const loadRegionalOptions = async () => {
    try {

      // Get all regionals
      const { data } = await api.get("regionals/read");

      if (data.status === "success") setOptionsRegionalFilter(data.result as Regional[]);
      else setOptionsRegionalFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingRegionalFilter(false);
    }
  };

  /**
   * Load group options from the API.
   */
  const loadGroupOptions = async () => {
    try {

      // Get all regionals
      const { data } = await api.get("groups/read");

      if (data.status === "success") setOptionsGroupFilter(data.result as Group[]);
      else setOptionsGroupFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingGroupFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[], loadMore: boolean) => {
    setLoadingAlerts(true);
    paramsFilterRef.current = paramsData;

    if (loadMore) offsetRef.current! += recordLimitPerFetch;
    else offsetRef.current = 0;

    try {
      const mappedParams = paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      const { data } = await api.post("alerts/read-history", {
        ...mappedParams,
        administrator: false,
        offset: offsetRef.current,
        limit: recordLimitPerFetch,
        [ParamNameTypes.UTC_OFFSET]: utils.getUtcOffset()
      }) as { data: StandardReturnObject<ProcessedAlertHistory[]>};

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));
      const validParamsDataWithoutDate = validParamsData.filter(
        (item) => item.paramName !== paramNameStartDate && item.paramName !== paramNameEndDate
      );

      if (data.status !== "success" || !data.result) {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

        return { status: "error" } as IOnFilterReturn;
      }

      const startDateValue = validParamsData.find((item) => item.paramName === paramNameStartDate)?.paramValue as string;
      const finishDateValue = validParamsData.find((item) => item.paramName === paramNameEndDate)?.paramValue as string;

      // Check if there are more records
      const returnedRecords = data.result ?? [];

      onFilter(
        validParamsDataWithoutDate.length,
        returnedRecords,
        startDateValue,
        finishDateValue,
        data.pagination
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingAlerts(false);
    }
  };
  // endregion Functions
  // region Effects

  // Load options from the API
  useEffect(() => {

    Promise.all([
      loadVehicleOptions(),
      loadVehicleTypeOptions(),
      loadAlertTypeOptions(),
      loadDriverOptions(),
      loadPlantOptions(),
      loadRegionalOptions(),
      loadGroupOptions()
    ]).then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (offsetRef.current === undefined) {

      return;
    }

    handleFilter(paramsFilterRef.current, true).then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreToggle]);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingAlerts} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.ALERT_HISTORY}
        onFilter={(paramsData) => handleFilter(paramsData, false)}
        multipleSelectionOptions={[
          vehicleFilterOptions,
          vehicleTypeFilterOptions,
          driverFilterOptions,
          alertTypeFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions
        ]}
        dateRangeOptions={dateRangeOptions}
      />
      <ModalFormScheduledReportDescription
        open={generateScheduleReport.openModal}
        onClose={() => generateScheduleReport.onCloseModal()}
        filter={{
          application_type_id: FilterApplicationTypesID.ALERT_HISTORY,
          filter_options: paramsFilterRef.current
        }}
        additionalFilterOptions={[]}
      />
    </>
  );
};

export default QueryFilterAlertHistory;
